<template>
    <div>
        <UseFiltersTemplate>
            <div class="grid items-start gap-6">
                <div class="grid gap-2">
                    <label class="text-sm font-semibold text-gray-800 dark:text-white">Category</label>
                    <div class="flex flex-wrap gap-2">
                        <button v-for="(category, index) in categories" :key="index" :class="cn('dark:text-gray-400 bg-[#f5f5f5] dark:bg-[#2c2e32] hover:bg-neutral-900 dark:hover:bg-[#3b3d41] hover:text-white transition transition-colors duration-300 py-2 px-4 text-sm font-medium rounded-md', { 'bg-neutral-900 dark:bg-[#3b3d41] text-white dark:text-white': tempFilters.category.includes(category.name) })" @click="toggleTempCategoryFilter(category.name)">
                            {{ category.name }}
                        </button>
                    </div>
                </div>
                <div class="grid gap-2">
                    <label class="text-sm font-semibold text-gray-800 dark:text-white">Rarity</label>
                    <div class="flex flex-wrap gap-2">
                        <button v-for="(rarity, index) in rarities" :key="index" :class="cn('dark:text-gray-400 bg-[#f5f5f5] dark:bg-[#2c2e32] hover:bg-neutral-900 dark:hover:bg-[#3b3d41] hover:text-white transition transition-colors duration-300 py-2 px-4 text-sm font-medium rounded-md', { 'bg-neutral-900 dark:bg-[#3b3d41] text-white dark:text-white': tempFilters.rarity.includes(rarity.name) })" @click="toggleTempRarityFilter(rarity.name)">
                            {{ rarity.name }}
                        </button>
                    </div>
                </div>
                <div class="grid gap-2">
                    <label class="text-sm font-semibold text-gray-800 dark:text-white">Demand</label>
                    <div class="flex flex-wrap gap-2">
                        <button v-for="(demand, index) in demands" :key="index" :class="cn('dark:text-gray-400 bg-[#f5f5f5] dark:bg-[#2c2e32] hover:bg-neutral-900 dark:hover:bg-[#3b3d41] hover:text-white transition transition-colors duration-300 py-2 px-4 text-sm font-medium rounded-md', { 'bg-neutral-900 dark:bg-[#3b3d41] text-white dark:text-white': tempFilters.demand.includes(demand.name) })" @click="toggleTempDemandFilter(demand.name)">
                            {{ demand.name }}
                        </button>
                    </div>
                </div>
                <div class="grid gap-2">
                    <label class="text-sm font-semibold text-gray-800 dark:text-white">Value</label>
                    <div class="flex gap-x-4">
                        <div class="grid gap-2 flex-1">
                            <label class="text-sm font-semibold text-gray-800 dark:text-white" for="minValue">Min</label>
                            <InputNumber id="minValue" name="min" v-model="tempFilters.minValue" />
                        </div>
                        <div class="grid gap-2 flex-1">
                            <label class="text-sm font-semibold text-gray-800 dark:text-white" for="maxValue">Max</label>
                            <InputNumber id="maxValue" name="max" v-model="tempFilters.maxValue" />
                        </div>
                    </div>
                </div>
                <div class="grid gap-2">
                    <label class="text-sm font-semibold text-gray-800 dark:text-white">Tradeable?</label>
                    <ShadRadioGroup v-model="tempFilters.tradeable" default-value="">
                        <div class="flex items-center space-x-2">
                            <ShadRadioGroupItem id="t1" value="true" />
                            <ShadLabel for="t1">Yes</ShadLabel>
                        </div>
                        <div class="flex items-center space-x-2">
                            <ShadRadioGroupItem id="t2" value="false" />
                            <ShadLabel for="t2">No</ShadLabel>
                        </div>
                        <div class="flex items-center space-x-2">
                            <ShadRadioGroupItem id="t3" value="" />
                            <ShadLabel for="t3">All</ShadLabel>
                        </div>
                    </ShadRadioGroup>
                </div>
            </div>
        </UseFiltersTemplate>
        <UseFilterTriggerTemplate>
            <div class="relative">
                <ShadButton variant="outline" :class="{ 'border-0 ring-2 ring-inset ring-black dark:ring-white': appliedFilterCount > 0 }">
                    <LucideSlidersHorizontal :size="14" />
                    <span class="ml-2">Filters</span>
                </ShadButton>
                <span v-if="appliedFilterCount > 0" class="absolute top-[-8px] right-[-8px] bg-black dark:bg-white dark:text-black text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                    {{ appliedFilterCount }}
                </span>
            </div>
        </UseFilterTriggerTemplate>
        <h1 class="text-lg text-gray-900 dark:text-white font-semibold">Items</h1>
        <div class="mt-3">
            <div class="flex gap-x-2.5">
                <ShadInput class="flex-1" placeholder="Search by name..." v-model="filters.name" />
                <ShadDialog v-if="viewport.isGreaterThan('sm')" v-model:open="isOpen">
                    <ShadDialogTrigger as-child>
                        <FilterTrigger />
                    </ShadDialogTrigger>
                    <ShadDialogContent class="block sm:max-w-[600px] p-0">
                        <ShadDialogHeader class="py-6 px-5 border-b border-neutral-200 dark:border-white/5">
                            <ShadDialogTitle>Filters</ShadDialogTitle>
                        </ShadDialogHeader>
                        <div class="py-6 px-6">
                            <Filters />
                        </div>
                        <ShadDialogFooter class="py-4 px-5 sm:justify-between border-t border-neutral-200 dark:border-white/5">
                            <ShadButton variant="outline" @click="resetFilters">
                                Reset
                            </ShadButton>
                            <ShadButton @click="applyFilters">
                                Apply
                            </ShadButton>
                        </ShadDialogFooter>
                    </ShadDialogContent>
                </ShadDialog>
                <ShadDrawer v-else v-model:open="isOpen">
                    <ShadDrawerTrigger as-child>
                        <FilterTrigger />
                    </ShadDrawerTrigger>
                    <ShadDrawerContent>
                        <ShadDrawerHeader class="text-left px-6">
                            <ShadDrawerTitle>Filters</ShadDrawerTitle>
                        </ShadDrawerHeader>
                        <div class="pt-2 pb-6 px-6 overflow-y-auto max-h-[400px] sm:max-h-[550px]">
                            <Filters />
                        </div>
                        <ShadDrawerFooter class="py-6 px-6 border-t border-neutral-200 dark:border-white/5 flex-row justify-between">
                            <ShadButton variant="outline" @click="resetFilters">
                                Reset
                            </ShadButton>
                            <ShadButton @click="applyFilters">
                                Apply
                            </ShadButton>
                        </ShadDrawerFooter>
                    </ShadDrawerContent>
                </ShadDrawer>
            </div>
            <div class="mt-4">
                <Pagination v-model="filters.page" :total-items="items?.totalCount" :items-per-page="filters.limit" />
                <div class="grid grid-cols-2 sm:auto-fill-[160px] md:auto-fill-[190px] gap-3" :class="{'my-4': items?.totalCount! > filters.limit}" v-if="items?.data.length! > 0">
                    <NuxtLink :to="`/mm2/item/${item.id}`" v-for="item in items?.data" :key="item.id">
                        <Item class="mt-0" :icon="item.icon" :name="item.name" :category="item.category" :rarity="item.rarity" :value="item.value" :date="item.created_at" />
                    </NuxtLink>
                </div>
                <Pagination v-model="filters.page" :total-items="items?.totalCount" :items-per-page="filters.limit" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash-es'
import { cn } from '~/lib/utils'
import type { ItemData } from './index.vue'
import Item from './components/Item.vue'

interface ApiResponse {
    data: ItemData[];
    currentPage: number;
    isFirstPage: boolean;
    isLastPage: boolean;
    previousPage: number | null;
    nextPage: number | null;
    pageCount: number;
    totalCount: number;
}

const [UseFiltersTemplate, Filters] = createReusableTemplate()
const [UseFilterTriggerTemplate, FilterTrigger] = createReusableTemplate()
const viewport = useViewport()

const isOpen = ref(false)

const defaultFilters = {
    name: '',
    page: 1,
    limit: 30,
    category: [] as string[],
    rarity: [] as string[],
    demand: [] as string[],
    minValue: undefined,
    maxValue: undefined,
    tradeable: ''
}

const filters = ref<{
    name: string;
    page: number;
    limit: number;
    category: string[];
    rarity: string[];
    demand: string[];
    minValue: number | string | undefined;
    maxValue: number | string | undefined;
    tradeable: boolean | string;
}>(cloneDeep(defaultFilters))

const tempFilters = ref(cloneDeep(defaultFilters))

// Compute the number of applied filters
const appliedFilterCount = computed(() => {
    let count = 0;
    if (filters.value.category.length > 0) count += filters.value.category.length; // Non-empty category array
    if (filters.value.rarity.length > 0) count += filters.value.rarity.length; // Non-empty rarity array
    if (filters.value.demand.length > 0) count += filters.value.demand.length; // Non-empty demand array
    if (filters.value.minValue) count++; // minValue is set
    if (filters.value.maxValue) count++; // maxValue is set
    if (filters.value.tradeable) count++; // tradeable is set
    return count;
});

const { data: items } = await useFetch<ApiResponse>('/api/mm2/items', {
    query: {
        name: computed(() => filters.value.name !== '' ? filters.value.name : undefined),
        category: computed(() => filters.value.category),
        rarity: computed(() => filters.value.rarity),
        demand: computed(() => filters.value.demand),
        minValue: computed(() => filters.value.minValue !== '' ? filters.value.minValue : undefined),
        maxValue: computed(() => filters.value.maxValue !== '' ? filters.value.maxValue : undefined),
        tradeable: computed(() => filters.value.tradeable !== '' ? filters.value.tradeable : undefined),
        page: computed(() => filters.value.page),
        limit: filters.value.limit
    }
})

const { data: rarities } = await useFetch<{ name: string }[]>('/api/mm2/rarities')
const { data: categories } = await useFetch<{ name: string }[]>('/api/mm2/categories')
const { data: demands } = await useFetch<{ name: string }[]>('/api/demands')

const applyFilters = () => {
    filters.value = cloneDeep(tempFilters.value)
    isOpen.value = false
}

const resetFilters = () => {
    tempFilters.value = cloneDeep(defaultFilters)
}

const toggleTempCategoryFilter = (category: string) => {
    if (tempFilters.value.category.includes(category)) {
        tempFilters.value.category = tempFilters.value.category.filter((c: string) => c !== category)
    } else {
        tempFilters.value.category.push(category)
    }
}

const toggleTempRarityFilter = (rarity: string) => {
    if (tempFilters.value.rarity.includes(rarity)) {
        tempFilters.value.rarity = tempFilters.value.rarity.filter((c: string) => c !== rarity)
    } else {
        tempFilters.value.rarity.push(rarity)
    }
}

const toggleTempDemandFilter = (demand: string) => {
    if (tempFilters.value.demand.includes(demand)) {
        tempFilters.value.demand = tempFilters.value.demand.filter((d: string) => d !== demand)
    } else {
        tempFilters.value.demand.push(demand)
    }
}

watch(() => {
    const { name, category, rarity, demand, minValue, maxValue, tradeable } = filters.value
    return { name, category, rarity, demand, minValue, maxValue, tradeable }
}, () => {
    filters.value.page = 1
}, { 
    deep: true 
});

useSeoMeta({
    title: 'Items'
})
</script>